import {
  AiProvenanceState,
  type FieldProvenanceDto,
  type FieldProvenanceInputDto,
  type MissionAssessmentDraftListItemDto,
  type MissionAssessmentDraftSelectDto,
  MissionAssessmentRequestStatus,
} from '@@types/api.types';
import { TooltipComponent } from '@components/core/tooltip.component';
import { Tile } from '@components/shared/tile.component';
import { Button } from '@components/ui/button';
import { useAppForm } from '@components/ui/form/form';
import { errorToast } from '@hooks/toast.hook';
import { cn } from '@lib/utils';
import {
  useCreateMissionAssessmentDraft,
  usePublishMissionAssessmentDraft,
  useUpdateMissionAssessmentDraft,
} from '@queries/mission-assessment-drafts.query';
import { useGetMissionAssessmentRequest } from '@queries/mission-assessment-requests.query';
import { useCreateMissionAssessment } from '@queries/mission-assessments.query';
import { useGetTechnologyMrlAssessment, useGetTechnologyTrlAssessment } from '@queries/technology.query';
import {
  convertMissionAssessmentDraftToFormData,
  missionAssessmentDraftCreateSchema,
} from '@schemas/mission-assessment-draft.schema';
import { type MissionAssessmentFormData, missionAssessmentFormSchema } from '@schemas/mission-assessment.schema';
import { useStore } from '@tanstack/react-form';
import { MAX_MRL_SCORE, MAX_TRL_SCORE } from '@utils/constants';
import { createFormInvalidHandler } from '@utils/form.util';
import type { ProvenanceAcceptEntry } from '@utils/mission-assessment-provenance.util';
import { SparklesIcon } from 'lucide-react';
import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FullAssessmentModal } from './full-assessment/full-assessment-modal.component';
import { MissionAssessmentDraftNavigation } from './mission-assessment-draft-navigation.component';
import { MissionAssessmentFormEffects } from './mission-assessment-form-effects.component';
import { MissionAssessmentFormHurdles } from './mission-assessment-form-hurdles.component';
import { MissionAssessmentFormInteroperability } from './mission-assessment-form-interoperability.component';
import { MissionAssessmentFormNotes } from './mission-assessment-form-notes.component';
import { missionAssessmentDraftFormOptions } from './mission-assessment-form-options';
import { MissionAssessmentFormRecommendedTechUses } from './mission-assessment-form-recommended-tech-uses.component';
import { MissionAssessmentFormServices } from './mission-assessment-form-services.component';

type MissionAssessmentFormCreateProps = {
  draftVersions?: never;
  mode: 'create';
  missionAssessmentDraft?: never;
  requestId: string;
  technologyId: string;
};

type MissionAssessmentFormUpdateProps = {
  draftVersions?: MissionAssessmentDraftListItemDto[];
  isTransitioning?: boolean;
  mode: 'update';
  missionAssessmentDraft: MissionAssessmentDraftSelectDto;
  requestId?: string;
  technologyId: string;
};

type MissionAssessmentFormProps = MissionAssessmentFormCreateProps | MissionAssessmentFormUpdateProps;

type FormMeta = {
  submitAction: 'draft' | 'complete' | null;
};

const defaultMeta: FormMeta = {
  submitAction: null,
};

const calculateNextScores = (args: { currentMrlScore?: number; currentTrlScore?: number }) => {
  const { currentMrlScore = 0, currentTrlScore = 0 } = args;
  const nextMrlScore = currentMrlScore < MAX_MRL_SCORE ? currentMrlScore + 1 : undefined;
  const nextTrlScore = currentTrlScore < MAX_TRL_SCORE ? currentTrlScore + 1 : undefined;
  return { nextMrlScore, nextTrlScore };
};

export const MissionAssessmentForm: React.FC<MissionAssessmentFormProps> = (props) => {
  const { draftVersions, missionAssessmentDraft, mode, technologyId } = props;
  const isTransitioning = mode === 'update' ? props.isTransitioning : undefined;
  const navigate = useNavigate();
  const submitActionRef = useRef<FormMeta['submitAction']>(null);
  const [isFullAssessmentOpen, setIsFullAssessmentOpen] = useState(false);

  // Get requestId based on mode
  const requestId = mode === 'create' ? props.requestId : missionAssessmentDraft.request?.id;

  // Fetch the request to check if it's been withdrawn (cancelled)
  const { data: missionAssessmentRequest } = useGetMissionAssessmentRequest(requestId);
  const isRequestWithdrawn = missionAssessmentRequest?.status === MissionAssessmentRequestStatus.CANCELLED;

  const { data: trlAssessment } = useGetTechnologyTrlAssessment(technologyId);
  const { data: mrlAssessment } = useGetTechnologyMrlAssessment(technologyId);

  const { mutateAsync: createMissionAssessment } = useCreateMissionAssessment();
  const { mutateAsync: createMissionAssessmentDraft } = useCreateMissionAssessmentDraft();
  const { mutateAsync: updateMissionAssessmentDraft } = useUpdateMissionAssessmentDraft(missionAssessmentDraft?.id);
  const { mutateAsync: publishMissionAssessmentDraft } = usePublishMissionAssessmentDraft();

  // Field-level AI provenance (ADR-0007): collect the provenance keys of fields/items
  // accepted from AI this session, then send them on the next save (each marks its
  // field AI_ASSISTED). The ref is the authoritative store read at save; the state
  // mirror exists so accepting a Suggestion re-renders the form and shows the badge
  // immediately, before save.
  const acceptedProvenanceRef = useRef<Record<string, FieldProvenanceInputDto>>({});
  const [sessionProvenanceKeys, setSessionProvenanceKeys] = useState<string[]>([]);
  const recordProvenanceAccept = (entries: ProvenanceAcceptEntry[]) => {
    for (const entry of entries) {
      acceptedProvenanceRef.current[entry.key] = { llmLogId: entry.llmLogId };
    }
    setSessionProvenanceKeys(Object.keys(acceptedProvenanceRef.current));
  };

  // Badge source for the form: persisted draft provenance plus fields accepted from
  // AI in this session (shown immediately). Session entries have no generatedAt yet —
  // the form badge uses the explanatory `context="form"` tooltip, not a date.
  const loadedProvenance = mode === 'update' ? missionAssessmentDraft.provenance : undefined;
  const formProvenance: Record<string, FieldProvenanceDto> = { ...(loadedProvenance ?? {}) };
  for (const key of sessionProvenanceKeys) {
    formProvenance[key] = formProvenance[key] ?? { state: AiProvenanceState.AI_ASSISTED };
  }

  const handleCreateMissionAssessment = async (value: MissionAssessmentFormData) => {
    if (mode !== 'create') {
      return;
    }

    const validationResult = missionAssessmentFormSchema.safeParse(value);

    if (!validationResult.success) {
      errorToast({ description: 'Please fill out all required fields correctly.' });
      return;
    }

    const createdAssessment = await createMissionAssessment({
      ...validationResult.data,
      provenanceInput: acceptedProvenanceRef.current,
      requestId: props.requestId,
      technologyId,
    });
    navigate(`/technology/${technologyId}?tab=ma&maId=${createdAssessment.id}`);
  };

  const handleCreateMissionAssessmentDraft = async (value: MissionAssessmentFormData) => {
    if (mode !== 'create') {
      return;
    }

    const validationResult = missionAssessmentDraftCreateSchema.safeParse(value);

    if (!validationResult.success) {
      errorToast({ description: 'Please fill out all required fields correctly.' });
      return;
    }

    await createMissionAssessmentDraft({
      ...validationResult.data,
      provenanceInput: acceptedProvenanceRef.current,
      requestId: props.requestId,
      technologyId,
    });
    navigate(`/assessments`);
  };

  const handleUpdateMissionAssessmentDraft = async (value: MissionAssessmentFormData) => {
    if (mode !== 'update') {
      return;
    }

    const validationResult = missionAssessmentDraftCreateSchema.safeParse(value);

    if (!validationResult.success) {
      errorToast({ description: 'Please fill out all required fields correctly.' });
      return;
    }

    await updateMissionAssessmentDraft({ ...validationResult.data, provenanceInput: acceptedProvenanceRef.current });
    navigate(`/assessments`);
  };

  const handlePublishMissionAssessmentDraft = async (value: MissionAssessmentFormData) => {
    if (mode !== 'update') {
      return;
    }

    // For publishing, we need to transform to the API format and validate strictly
    const validationResult = missionAssessmentFormSchema.safeParse(value);

    if (!validationResult.success) {
      errorToast({ description: 'Please fill out all required fields correctly.' });
      return;
    }

    if (!requestId || !missionAssessmentRequest || isRequestWithdrawn) {
      errorToast({ description: 'This request has been cancelled and cannot be published.' });
      return;
    }

    const publishedAssessment = await publishMissionAssessmentDraft({
      missionAssessmentDraftId: missionAssessmentDraft.id,
      missionAssessmentData: {
        ...validationResult.data,
        provenanceInput: acceptedProvenanceRef.current,
        technologyId,
        requestId,
      },
    });
    navigate(`/technology/${technologyId}?tab=ma&maId=${publishedAssessment.id}`);
  };

  const nextScores = calculateNextScores({
    currentMrlScore: mrlAssessment?.score,
    currentTrlScore: trlAssessment?.score,
  });

  // Prepare form data based on mode
  const getFormDefaultValues = (): MissionAssessmentFormData => {
    if (mode === 'update') {
      // Update mode - editing an existing draft
      const convertedData = convertMissionAssessmentDraftToFormData(props.missionAssessmentDraft);

      return {
        ...convertedData,
        data: {
          ...convertedData.data,
          hurdles: {
            ...convertedData.data?.hurdles,
            nextMrlScore: convertedData.data?.hurdles?.nextMrlScore ?? nextScores.nextMrlScore,
            nextTrlScore: convertedData.data?.hurdles?.nextTrlScore ?? nextScores.nextTrlScore,
          },
        },
      };
    }

    // Create mode - starting fresh
    return {
      ...missionAssessmentDraftFormOptions.defaultValues,
      data: {
        ...missionAssessmentDraftFormOptions.defaultValues.data,
        hurdles: {
          ...missionAssessmentDraftFormOptions.defaultValues.data?.hurdles,
          nextMrlScore: nextScores.nextMrlScore,
          nextTrlScore: nextScores.nextTrlScore,
        },
      },
    };
  };

  const defaultValues = getFormDefaultValues();
  const form = useAppForm({
    canSubmitWhenInvalid: true,
    defaultValues,
    onSubmitMeta: defaultMeta,
    validators: {
      // Only validate strictly when submitting as complete
      // Note: Type casts needed because schemas have transforms that change output type
      onSubmit: ({ formApi }) => {
        if (submitActionRef.current === 'complete') {
          const errors = formApi.parseValuesWithSchema(
            missionAssessmentFormSchema as unknown as Parameters<typeof formApi.parseValuesWithSchema>[0],
          );
          if (errors) return errors;
        } else {
          const errors = formApi.parseValuesWithSchema(
            missionAssessmentDraftCreateSchema as unknown as Parameters<typeof formApi.parseValuesWithSchema>[0],
          );
          if (errors) return errors;
        }

        return undefined;
      },
    },
    onSubmit: async ({ meta, value }) => {
      if (mode === 'create') {
        if (meta.submitAction === 'complete') {
          return await handleCreateMissionAssessment(value);
        } else {
          return await handleCreateMissionAssessmentDraft(value);
        }
      }

      if (mode === 'update') {
        if (meta.submitAction === 'complete') {
          return await handlePublishMissionAssessmentDraft(value);
        } else {
          return await handleUpdateMissionAssessmentDraft(value);
        }
      }
    },
    onSubmitInvalid: createFormInvalidHandler(),
  });

  const handleSubmitAsDraft = async () => {
    submitActionRef.current = 'draft';
    await form.handleSubmit({ submitAction: 'draft' });
  };

  const handleSubmitAsComplete = async () => {
    submitActionRef.current = 'complete';
    await form.handleSubmit({ submitAction: 'complete' });
  };

  const isSubmitting = useStore(form.store, (state) => state.isSubmitting);
  const isSubmittingDraft = isSubmitting && submitActionRef.current === 'draft';
  const isSubmittingComplete = isSubmitting && submitActionRef.current === 'complete';

  return (
    <Tile className="p-0">
      <form.AppForm>
        <div className="space-y-6 p-4">
          <MissionAssessmentFormServices
            form={form}
            provenance={formProvenance}
            submitAction={submitActionRef.current}
            technologyId={technologyId}
          />

          <div className="h-px bg-gray-200 w-full" />

          <MissionAssessmentFormEffects
            form={form}
            provenance={formProvenance}
            technologyId={technologyId}
          />

          <div className="h-px bg-gray-200 w-full" />

          <MissionAssessmentFormInteroperability
            form={form}
            provenance={formProvenance}
            submitAction={submitActionRef.current}
            technologyId={technologyId}
          />

          <div className="h-px bg-gray-200 w-full" />

          <MissionAssessmentFormHurdles
            form={form}
            currentMrlScore={mrlAssessment?.score}
            currentTrlScore={trlAssessment?.score}
            onProvenanceAccept={recordProvenanceAccept}
            provenance={formProvenance}
            technologyId={technologyId}
          />

          <div className="h-px bg-gray-200 w-full" />

          <MissionAssessmentFormNotes
            form={form}
            onProvenanceAccept={recordProvenanceAccept}
            provenance={formProvenance}
            technologyId={technologyId}
          />

          <div className="h-px bg-gray-200 w-full" />

          <MissionAssessmentFormRecommendedTechUses
            form={form}
            provenance={formProvenance}
          />
        </div>

        <div className="h-4" />
        <div className="sticky bottom-0 left-1/2 z-10 transform p-4 bg-white rounded-b-xl border-t border-gray-200 w-full flex flex-col items-center gap-4">
          <div className={cn('flex flex-1 w-full items-center justify-between gap-4')}>
            <div className="flex items-center gap-4">
              <Button
                disabled={isSubmitting}
                onClick={() => setIsFullAssessmentOpen(true)}
                type="button"
                variant="link"
              >
                <SparklesIcon size={14} />
                Generate Assessment
              </Button>

              {mode === 'update' && draftVersions && missionAssessmentDraft && (
                <MissionAssessmentDraftNavigation
                  form={form}
                  currentDraftId={missionAssessmentDraft.id}
                  isTransitioning={isTransitioning}
                  technologyId={technologyId}
                  versions={draftVersions}
                />
              )}
            </div>

            <div className="flex items-center gap-4">
              {!isSubmittingComplete && (
                <Button
                  variant="secondary"
                  className="min-w-36"
                  loading={isSubmittingDraft}
                  onClick={handleSubmitAsDraft}
                >
                  Save as Draft
                </Button>
              )}

              {!isSubmittingDraft &&
                (isRequestWithdrawn ? (
                  <TooltipComponent
                    side="top"
                    trigger={
                      <Button
                        className="min-w-36"
                        disabled
                        loading={isSubmittingComplete}
                        onClick={handleSubmitAsComplete}
                      >
                        Submit Assessment
                      </Button>
                    }
                  >
                    This request has been cancelled and the assessment cannot be submitted
                  </TooltipComponent>
                ) : (
                  <Button
                    className="min-w-36"
                    loading={isSubmittingComplete}
                    onClick={handleSubmitAsComplete}
                  >
                    Submit Assessment
                  </Button>
                ))}
            </div>
          </div>
        </div>

        {isFullAssessmentOpen && (
          <FullAssessmentModal
            form={form}
            isOpen={isFullAssessmentOpen}
            onClose={() => setIsFullAssessmentOpen(false)}
            onProvenanceAccept={recordProvenanceAccept}
            technologyId={technologyId}
          />
        )}
      </form.AppForm>
    </Tile>
  );
};
